import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import SEO from '../components/SEO';
import knowledgeSvgDark from '../assets/knowledgeHubDiagram.svg';
import knowledgeSvgLight from '../assets/knowledgeHubDiagramLight.svg';
import '../styles/productPage.css';

const TABS = ['overview', 'features', 'howItWorks', 'useCases'];

const KnowledgeHub = () => {
  const { t } = useTranslation('knowledgeHub');
  const { language } = useLanguage();
  const { theme } = useTheme();
  const knowledgeSvg = theme === 'light' ? knowledgeSvgLight : knowledgeSvgDark;
  const [activeTab, setActiveTab] = useState(0);

  /* safe array from locale */
  const tArr = (key) => {
    const val = t(key, { returnObjects: true });
    return Array.isArray(val) ? val : [];
  };

  return (
    <div className="product-page">
      <SEO
        title="E1 Knowledge Hub – Enterprise Knowledge Intelligence | EWANDS"
        description="Ingest your documents, policies, and research into a governed AI knowledge layer — then let AI chat, investigate, uncover patterns, and automate actions."
        keywords="E1 Knowledge Hub, enterprise knowledge management, semantic search, AI knowledge base, document intelligence, EWANDS"
        canonicalPath={`/${language}/knowledge-hub`}
      />

      {/* Hero */}
      <section className="product-hero">
        <div className="container">
          <div className="product-hero-inner">
            <span className="product-hero-tag">{t('overview.tag')}</span>
            <h1>{t('hero.title')}</h1>
            <p className="product-hero-subtitle">{t('hero.subtitle')}</p>
            <p className="product-hero-desc">{t('hero.description')}</p>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="product-tabs-section">
        <div className="product-tab-bar-wrapper">
          <div className="container">
            <div className="product-tab-bar">
              {TABS.map((tab, i) => (
                <button
                  key={tab}
                  className={`product-tab-btn${activeTab === i ? ' active' : ''}`}
                  onClick={() => setActiveTab(i)}
                >
                  {t(`tabs.${tab}`)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="product-tab-content">
          <div className="container">

            {/* ── Overview ───────────────────────────────── */}
            {activeTab === 0 && (
              <div>
                <span className="pp-section-tag">{t('overview.tag')}</span>
                <h2 className="pp-section-heading">{t('overview.heading')}</h2>
                <p className="pp-section-desc">{t('overview.description')}</p>
                <div className="pp-highlights">
                  {['h1','h2','h3','h4'].map(k => (
                    <div className="pp-highlight-card" key={k}>
                      <div className="pp-highlight-label">{t(`overview.highlights.${k}.label`)}</div>
                      <div className="pp-highlight-desc">{t(`overview.highlights.${k}.desc`)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Features ───────────────────────────────── */}
            {activeTab === 1 && (
              <div>
                <span className="pp-section-tag">{t('features.tag')}</span>
                <h2 className="pp-section-heading">{t('features.heading')}</h2>
                <div className="pp-feature-grid">
                  {['semanticSearch','fileSearch','documentIngestion','intelligentAssistant','patternDiscovery','versionControl','accessControl','analytics'].map(key => (
                    <div className="pp-feature-card" key={key}>
                      <span className="pp-feature-tag">{t(`features.items.${key}.tag`)}</span>
                      <div className="pp-feature-title">{t(`features.items.${key}.title`)}</div>
                      <div className="pp-feature-desc">{t(`features.items.${key}.description`)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── How It Works ───────────────────────────── */}
            {activeTab === 2 && (
              <div>
                <span className="pp-section-tag">{t('howItWorks.tag')}</span>
                <h2 className="pp-section-heading">{t('howItWorks.heading')}</h2>
                <p className="pp-section-desc">{t('howItWorks.description')}</p>

                {/* Diagram */}
                <img
                  src={knowledgeSvg}
                  alt="E1 Knowledge Hub Pipeline Diagram"
                  style={{ width: '100%', borderRadius: '12px', marginTop: '1.5rem', marginBottom: '2.5rem' }}
                />

                {/* Ingestion pipeline stages */}
                <h3 className="pp-sub-heading">{t('howItWorks.pipeline.heading')}</h3>
                <div className="pp-arch-stages" style={{ marginTop: '1rem', marginBottom: '2.5rem' }}>
                  {['parse','chunk','embed','index'].map(key => (
                    <div className="pp-arch-stage" key={key}>
                      <span className="pp-arch-stage-step">{t(`howItWorks.pipeline.stages.${key}.step`)}</span>
                      <span className="pp-arch-stage-tag">{t(`howItWorks.pipeline.stages.${key}.tag`)}</span>
                      <div className="pp-arch-stage-title">{t(`howItWorks.pipeline.stages.${key}.title`)}</div>
                      <div className="pp-arch-stage-desc">{t(`howItWorks.pipeline.stages.${key}.description`)}</div>
                    </div>
                  ))}
                </div>

                {/* AI layer actions */}
                <h3 className="pp-sub-heading">{t('howItWorks.aiLayer.heading')}</h3>
                <div className="pp-feature-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginTop: '1rem', marginBottom: '2rem' }}>
                  {['aiChat','aiInvestigation','workflowAutomation'].map(key => (
                    <div className="pp-feature-card" key={key}>
                      <span className="pp-feature-tag">{t(`howItWorks.aiLayer.items.${key}.tag`)}</span>
                      <div className="pp-feature-title">{t(`howItWorks.aiLayer.items.${key}.title`)}</div>
                      <div className="pp-feature-desc">{t(`howItWorks.aiLayer.items.${key}.description`)}</div>
                    </div>
                  ))}
                </div>

                {/* Outputs */}
                <h3 className="pp-sub-heading">{t('howItWorks.outputs.heading')}</h3>
                <ul className="pp-bullet-list" style={{ marginTop: '0.5rem' }}>
                  {tArr('howItWorks.outputs.items').map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* ── Use Cases ──────────────────────────────── */}
            {activeTab === 3 && (
              <div>
                <span className="pp-section-tag">{t('useCases.tag')}</span>
                <h2 className="pp-section-heading">{t('useCases.heading')}</h2>
                <div className="pp-feature-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                  {['hr','legal','operations','strategy','compliance','finance'].map(key => (
                    <div className="pp-feature-card" key={key}>
                      <span className="pp-feature-tag">{t(`useCases.items.${key}.tag`)}</span>
                      <div className="pp-feature-title">{t(`useCases.items.${key}.title`)}</div>
                      <div className="pp-feature-desc">{t(`useCases.items.${key}.description`)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      </section>
    </div>
  );
};

export default KnowledgeHub;
